import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { CompanyUserFormScope } from 'mormaii-web-presentation/company-user-form'

import { Modal } from '../../../../components/dialog'
import { Button, ChoiceFilter, FormField } from '../../../../components/filters'
import { Spinner } from '../../../../components/ui'

import Css from './company-user-form.module.scss'

/**
 * O cadastro de um usuario da empresa.
 *
 * Administrador e profissional sao o MESMO formulario: o que o profissional
 * acrescenta e o bloco do conselho, e duplicar a tela para isso seria manter cinco
 * campos em dois lugares.
 *
 * Nao ha campo de senha. Ela e gerada e vai por e-mail, como no administrador que
 * nasce junto com a empresa - quem cadastra nao escolhe a senha de outra pessoa.
 */
export function CompanyUserFormView(props: ViewProps<CompanyUserFormScope>): JSX.Element {
    const submit = (event: Event) => {
        event.preventDefault()
        void props.scope.onSave()
    }

    return (
        <Modal
            wide
            title={props.scope.title}
            onClose={() => void props.scope.onClose()}
            footer={
                <>
                    <Button variant="ghost" onClick={() => void props.scope.onClose()}>
                        Cancelar
                    </Button>
                    {/*
                        No modo VINCULO, enquanto ninguem foi escolhido nao ha o que
                        salvar - e um botao aceso ali so poderia recusar.
                    */}
                    <Show when={props.scope.mode === 'cadastro' || props.scope.existingName}>
                        <Button
                            form="company-user"
                            type="submit"
                            disabled={props.scope.saving || !!props.scope.existingBlock}
                        >
                            {props.scope.saving
                                ? 'Salvando…'
                                : props.scope.existingName
                                  ? 'Vincular à empresa'
                                  : 'Cadastrar'}
                        </Button>
                    </Show>
                </>
            }
        >
            <form id="company-user" onSubmit={submit} class={Css.form}>
                {/*
                    DUAS COISAS DIFERENTES com o mesmo fim, e a escolha vem antes.

                    Quem sabe que a pessoa ja esta na plataforma nao deveria precisar
                    descobrir isso digitando um e-mail e esperando a tela reagir: ele
                    diz "ja existe" e aponta na lista. Quem vai criar uma pessoa nova
                    segue no formulario de sempre.
                */}
                <ModePicker scope={props.scope} />

                <Show when={props.scope.mode === 'vinculo'}>
                    <CandidateList scope={props.scope} />

                    {/*
                        QUEM foi escolhido vem antes do que se pede sobre ele: um CPF
                        e uma profissao no alto da tela, com o nome da pessoa embaixo,
                        obrigam a ler de baixo para cima para saber de quem se trata.
                    */}
                    <Show when={props.scope.existingName}>
                        {(nome) => <ChosenPerson scope={props.scope} name={nome()} />}
                    </Show>
                </Show>

                {/*
                    O E-MAIL vem primeiro, e e ele que decide o resto da tela.

                    Digitado um e-mail que ja tem conta, os campos de pessoa somem:
                    quem ja esta na plataforma ja tem nome e telefone, e pedi-los de
                    novo faz parecer que vai nascer outra pessoa - alem de o que fosse
                    digitado ser descartado. Fica so o CPF, que e a prova de identidade.
                */}
                <Show when={props.scope.mode === 'cadastro'}>
                    <FormField
                        label="E-mail"
                        value={props.scope.email}
                        error={props.scope.emailError}
                        onChange={(value) => props.scope.onEmailChanged(value)}
                        wide
                    />
                </Show>

                {/*
                    Um so criterio para tudo o que e CADASTRO: achou conta com este
                    e-mail, nada disso e perguntado. Vale para quem vai ser vinculado
                    e para quem nao pode ser - nos dois casos nenhum destes campos
                    seria usado, e pedi-los faria a tela parecer que cria uma pessoa.
                */}
                <Show when={!props.scope.existingName && props.scope.mode === 'cadastro'}>
                    <FormField
                        label="Nome"
                        value={props.scope.firstName}
                        error={props.scope.firstNameError}
                        onChange={(value) => props.scope.onFirstNameChanged(value)}
                    />
                    <FormField
                        label="Sobrenome"
                        value={props.scope.lastName}
                        error={props.scope.lastNameError}
                        onChange={(value) => props.scope.onLastNameChanged(value)}
                    />
                    <FormField
                        label="Telefone"
                        value={props.scope.phone}
                        error={props.scope.phoneError}
                        onChange={(value) => props.scope.onPhoneChanged(value)}
                    />
                </Show>

                <Show
                    when={!props.scope.existingBlock && (props.scope.mode === 'cadastro' || props.scope.existingName)}
                >
                    <FormField
                        label="CPF"
                        value={props.scope.cpf}
                        error={props.scope.cpfError}
                        onChange={(value) => props.scope.onCpfChanged(value)}
                    />
                </Show>

                {/*
                    A PROFISSAO e perguntada tambem no vinculo, e de proposito.

                    Ela e do VINCULO: a mesma pessoa pode ser nutricionista num
                    cliente e preparadora fisica em outro, e ate 17/09/2026 cadastra-la
                    na segunda reescrevia, calada, a profissao da primeira. Vem
                    preenchida com a mais recente dela - sugestao, e nao resposta.

                    O REGISTRO de conselho, esse continua sendo da pessoa, e por isso
                    so aparece no cadastro: numero e UF nao sao desta empresa.
                */}
                <Show
                    when={
                        props.scope.professional &&
                        !props.scope.existingBlock &&
                        (props.scope.mode === 'cadastro' || props.scope.existingName)
                    }
                >
                    <p class={Css.section}>{props.scope.existingName ? 'Nesta empresa' : 'Registro profissional'}</p>

                    <div class={Css.wide}>
                        <ChoiceFilter
                            label="Profissão"
                            summary={props.scope.typeSummary}
                            placeholder="Selecione a profissão"
                            hasValue={!!props.scope.typeSummary}
                            onClear={() => void props.scope.onClearType()}
                            options={props.scope.types.map((type) => ({
                                label: type.label,
                                value: type.value,
                                current: type.current,
                                onSelect: type.onSelect
                            }))}
                        />
                    </div>

                    {/*
                        A ESPECIALIDADE, que so existe para profissao que tem uma.
                        "Medico" tem cinquenta e cinco; personal trainer e fisiologista
                        nao tem nenhuma, e ai o seletor nao aparece.
                    */}
                    <Show when={props.scope.specialties.length > 0}>
                        <div class={Css.wide}>
                            <ChoiceFilter
                                label="Especialidade"
                                summary={props.scope.specialtySummary}
                                placeholder="Selecione a especialidade"
                                hasValue={!!props.scope.specialtySummary}
                                onClear={() => void props.scope.onClearSpecialty()}
                                options={props.scope.specialties.map((type) => ({
                                    label: type.label,
                                    value: type.value,
                                    current: type.current,
                                    onSelect: type.onSelect
                                }))}
                            />
                        </div>
                    </Show>

                    {/*
                        O registro so aparece para profissao REGULAMENTADA - o conselho
                        mora nela, e a especialidade herda. Cobrar um numero de quem nao
                        tem conselho seria um campo impossivel de preencher, e ate
                        18/09/2026 esse caminho nunca tinha sido acionado: as 55 linhas
                        do catalogo eram todas do CRM. "Fisiologista" e a primeira a
                        exercita-lo, porque nao e profissao regulamentada no Brasil.
                    */}
                    <Show when={props.scope.regulated && !props.scope.existingName}>
                        <FormField
                            label="Número do registro"
                            placeholder="CRM, CRN, CREF…"
                            value={props.scope.registrationNumber}
                            onChange={(value) => props.scope.onRegistrationChanged(value)}
                        />
                        <FormField
                            label="UF do registro"
                            placeholder="SC"
                            value={props.scope.uf}
                            onChange={(value) => props.scope.onUfChanged(value)}
                        />
                    </Show>
                </Show>

                {/*
                    O rodape conta o que VAI acontecer, e sao duas coisas diferentes.

                    Prometer "senha gerada e enviada por e-mail" a quem ja tem conta e
                    falso: a conta e REAPROVEITADA, e a pessoa segue entrando com a
                    senha que ja tinha. Foi exatamente o que aconteceu com quem tentou.
                */}
                <Show when={props.scope.mode === 'cadastro' && props.scope.existingName}>
                    {(nome) => (
                        <div class={Css.notice} classList={{ [Css.informational]: !!props.scope.existingBlock }}>
                            <Show when={!props.scope.existingBlock} fallback={<p>{props.scope.existingBlock}</p>}>
                                <p>
                                    Este e-mail já é de <strong>{nome()}</strong> ({props.scope.existingCpf}), que já
                                    está na plataforma.
                                </p>
                                <p class={Css.noticeNote}>{REAPROVEITA}</p>
                            </Show>
                        </div>
                    )}
                </Show>

                {/*
                    A promessa da senha so vale para quem VAI NASCER: quem ja tem conta
                    segue entrando com a que sempre teve, e dizer o contrario foi
                    exatamente o que confundiu quem tentou.
                */}
                <Show when={props.scope.mode === 'cadastro' && !props.scope.existingName}>
                    <p class={Css.passwordNote}>
                        A senha é gerada pelo sistema e enviada por e-mail. No primeiro acesso ela deve ser trocada.
                    </p>
                </Show>

                <Show when={props.scope.formError}>{(message) => <p class={Css.error}>{message()}</p>}</Show>
            </form>
        </Modal>
    )
}

/**
 * Cadastrar alguem novo, ou vincular quem ja esta na plataforma.
 *
 * Duas abas pequenas em vez de duas telas: sao caminhos para o mesmo fim - essa
 * pessoa passar a fazer parte desta empresa -, e separa-las em dois botoes no menu
 * faria escolher o caminho antes de saber que existem dois.
 */
function ModePicker(props: { scope: CompanyUserFormScope }): JSX.Element {
    const item = (mode: 'cadastro' | 'vinculo', label: string) => (
        <button
            type="button"
            onClick={() => props.scope.onModeChanged(mode)}
            class={Css.path}
            classList={{ [Css.current]: props.scope.mode === mode }}
        >
            {label}
        </button>
    )

    return (
        <div class={Css.paths}>
            {item('cadastro', 'Cadastrar nova pessoa')}
            {item('vinculo', 'Já está na plataforma')}
        </div>
    )
}

/**
 * Quem ja esta na plataforma e da para vincular.
 *
 * A lista ja vem preenchida com a gente das OUTRAS empresas de quem administra -
 * fazer a pessoa digitar para descobrir que o nutricionista dela esta ali seria
 * esconder a resposta atras de uma pergunta.
 *
 * O campo procura por nome so dentro dessas empresas. Fora delas, e o e-mail ou o
 * CPF inteiros: percorrer a base de usuarios da plataforma diria quem mais esta
 * sendo acompanhado, e isso nao e de quem procura.
 */
function CandidateList(props: { scope: CompanyUserFormScope }): JSX.Element {
    return (
        <div class={Css.candidates} classList={{ [Css.hidden]: !!props.scope.existingName }}>
            <div class={Css.search}>
                <input
                    value={props.scope.candidateTerm}
                    onInput={(event) => props.scope.onCandidateTermChanged(event.currentTarget.value)}
                    placeholder="Nome nas suas empresas, ou e-mail/CPF completo"
                    class={Css.searchInput}
                />
                <Show when={props.scope.candidatesLoading}>
                    <Spinner class={Css.searching} />
                </Show>
            </div>

            <div class={Css.list}>
                <For
                    each={props.scope.candidates}
                    fallback={
                        <p class={Css.none}>
                            {props.scope.candidatesLoading
                                ? 'Procurando…'
                                : props.scope.candidateTerm
                                  ? 'Ninguém encontrado. Para alcançar quem não é de uma empresa sua, informe o e-mail ou o CPF completo.'
                                  : 'Ninguém das suas outras empresas para vincular aqui. Informe o e-mail ou o CPF completo de quem procura.'}
                        </p>
                    }
                >
                    {(candidato) => (
                        <button type="button" onClick={() => void candidato.onSelect()} class={Css.candidate}>
                            <span class={Css.candidateTexts}>
                                <span class={Css.candidateName}>{candidato.name}</span>
                                <span class={Css.candidateFacts}>
                                    {candidato.email} · {candidato.cpfMascarado}
                                </span>
                            </span>

                            <span class={Css.candidateTags}>
                                {/*
                                    De onde a pessoa saiu: quem esta numa empresa sua e
                                    alguem que quem procura ja conhece, e o que ele
                                    precisa e reconhece-la - nao descobri-la.
                                */}
                                <Show when={candidato.minha}>
                                    <span class={Css.sameOwner}>Outra empresa sua</span>
                                </Show>
                                <Show when={candidato.profissao}>
                                    {(profissao) => <span class={Css.candidateProfession}>{profissao()}</span>}
                                </Show>
                            </span>
                        </button>
                    )}
                </For>
            </div>
        </div>
    )
}

/**
 * O que acontece com quem JA tem conta - a frase que faltava quando isto começou.
 *
 * Quem cadastrava alguem que ja existia preenchia o formulario inteiro e concluia,
 * com razao, que tinha criado outra pessoa. Pior: o rodape prometia uma senha nova
 * por e-mail, que nunca chegaria, porque a conta e REAPROVEITADA.
 */
const REAPROVEITA =
    'Ela passa a fazer parte desta empresa também, sem criar outra conta e sem mudar nada do que já é ' +
    'dela: continua entrando com a mesma senha, com o mesmo nome e o mesmo telefone. Confirme o CPF dela.'

/** Quem foi escolhido na lista, e como desfazer a escolha. */
function ChosenPerson(props: { scope: CompanyUserFormScope; name: string }): JSX.Element {
    return (
        <div class={Css.notice}>
            <p class={Css.chosen}>
                <span>
                    <strong>{props.name}</strong> ({props.scope.existingCpf}) já está na plataforma.
                </span>

                <button type="button" onClick={() => void props.scope.onForgetCandidate()} class={Css.changePerson}>
                    Escolher outra pessoa
                </button>
            </p>
            <p class={Css.noticeNote}>{REAPROVEITA}</p>
        </div>
    )
}
